import {
    AccountType,
    ActionTypes,
    SelectOption,
    TransactionStatusCode,
    TransactionStatusCodeMap,
    doesWorkflowRejectCodeExist,
    getWorkflowRejectCodeMessage
} from '@tassat/common-definitions';
import { FieldValidationAction } from '@tassat/forms';
import { IFormValues } from '@tassat/forms/src/WalletForm/types';
import { AccountRecord, CustomerAddFirmAccountReq, CustomerModifyFirmAccountReq } from '@tassat/fred-definitions';
import { Dispatch, SetStateAction } from 'react';

import Worker from 'Lib/worker/PrimusWorker';

export const checkPendingTransactions = async (
    Account: string,
    dispatchAccessControlAction: Dispatch<FieldValidationAction<SelectOption>>
) => {
    try {
        dispatchAccessControlAction({ type: 'VALIDATING_FIELD' });
        const transactionResponse = await Worker.getInstance()
            .getTransactions({
                Condition: `Status|EQ|${TransactionStatusCodeMap[TransactionStatusCode.PENDING_APPROVAL].value}:SenderAddress|EQ|${Account}`,
                Sort: 'auditTime|DESC',
                PageNo: 1,
                RecordPerPage: 5
            }) || { data: undefined };

        if (transactionResponse.data && transactionResponse.data.TotalCount > 0) {
            return 'Unable to change access control setting due to pending transactions';
        } else {
            dispatchAccessControlAction({ type: 'SERVER_SUCCESS' });

            return;
        }
    } catch (error) {
        return 'Could not validate pending transactions, please try again';
    }
};

export const submitWalletForm = async (values: IFormValues, options: {
    actionType: ActionTypes;
    firmId: string;
    editWallet: AccountRecord;
    onClose: () => void;
    setSubmitError: Dispatch<SetStateAction<string>>;
}) => {
    const {
        actionType,
        firmId,
        editWallet,
        onClose,
        setSubmitError
    } = options;

    try {
        let response = { data: undefined };
        const [
            ACGId1,
            ACGId2,
            ACGId3
        ] = values.ACGMapping.map((acg) => acg.value as string);

        if (actionType === ActionTypes.add) {
            const sendValues: Omit<
                CustomerAddFirmAccountReq,
                'RequestId' |
                'RequesterId' |
                'RequestType' |
                'RequestPattern' |
                'FirmId' |
                'Account' |
                'Ticker' |
                'UUID'
            > & { LinkedAccountId: string } = {
                AccountType: AccountType.CLIENT_EOA,
                AccountName: values.AccountName,
                AccessControl: Boolean(values.AccessControl.value),
                LinkedAccountId: values.LinkedAccount.value.toString(),
                IsLiquidityFacility: values.IsLiquidityFacility,
                Email: values.Email,
                Notes: values.Notes,
                ACGId1: ACGId1 || '0',
                ACGId2: ACGId2 || '0',
                ACGId3: ACGId3 || '0',
                IsWire: false,
                IsClosed: false,
                IsPrimary: values.IsPrimary
            };
            response = await Worker.getInstance()
                .createFirmAccount(firmId, sendValues);
        } else if (actionType === ActionTypes.edit) {
            // TODO: WIP
            const sendValues: Omit<
                CustomerModifyFirmAccountReq,
                'RequestId' |
                'RequesterId' |
                'FirmId' |
                'AccountId' |
                'Account' |
                'AccountName1' |
                'AccountName2' |
                'ShortName' |
                'WalletName1' |
                'WalletName2' |
                'UUID'
            > & { LinkedAccountId: string } = {
                AccountType: AccountType.CLIENT_EOA,
                AccountName: values.AccountName,
                AccessControl: Boolean(values.AccessControl.value),
                LinkedAccountId: values.LinkedAccount.value.toString(),
                IsLiquidityFacility: values.IsLiquidityFacility,
                Email: values.Email,
                Notes: values.Notes,
                ACGId1: ACGId1 || '0',
                ACGId2: ACGId2 || '0',
                ACGId3: ACGId3 || '0',
                IsWire: editWallet?.IsWire,
                IsLocked: editWallet?.IsLocked,
                IsClosed: editWallet?.IsClosed,
                IsPrimary: values.IsPrimary
            };

            response = await Worker.getInstance()
                .updateFirmAccount(firmId, editWallet?.AccountId, sendValues);
        }

        if (response.data?.Status === 48) {
            onClose();
        } else if (response.data && doesWorkflowRejectCodeExist(response.data.Status)) {
            setSubmitError(`Could not ${actionType === ActionTypes.add ? 'add' : 'edit'} Account: ${getWorkflowRejectCodeMessage(response.data.Status)}`);
        }
    } catch (error) {
        console.error(error);
    }
};

export const getTransactions = (options: {
    Condition: string;
    Sort: string;
    PageNo: number;
    RecordPerPage: number;
}) => Worker.getInstance()
    .getTransactions(options);

export const updateFirmAccount = (
    firmId: string,
    accountId: string,
    values: any
) => Worker.getInstance()
    .updateFirmAccount(firmId, accountId, values);